import { useState } from "react";
import { useConfirm } from "@/components/ui/confirm-dialog";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@satyrsmc/shared/components";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ContactStatusFilter, ContactSortField, SortDirection } from "@satyrsmc/shared/client";
import type { ContactSearchParams } from "@satyrsmc/shared/client";
import { contactsToVCardFileAsync } from "@/lib/vcard";
import { Link } from "react-router-dom";
import { Plus, Search, Download, Upload, List, Trash2, UserPlus, ChevronDown } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { AddContactDialog } from "./AddContactDialog";
import { ImportContactsDialog } from "./ImportContactsDialog";
import { InviteUserDialog } from "../users/InviteUserDialog";
import { AddToMailingListDialog } from "./AddToMailingListDialog";
import { downloadContactsCsv, downloadContactsPdf } from "./contactUtils";
import { ContactDirectoryTable } from "./ContactDirectoryTable";
import {
  useContactsOptional,
  useInvalidateQueries,
  useContactsBulkUpdate,
  useDeleteContact,
  useContactsListFetcher,
  useUrlSearchSync,
} from "@/queries/hooks";

export function ContactsPanel() {
  const { confirm, confirmDialog } = useConfirm();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const invalidate = useInvalidateQueries();
  const bulkUpdateMutation = useContactsBulkUpdate();
  const deleteContactMutation = useDeleteContact();
  const fetchContactsList = useContactsListFetcher();

  // --- URL-derived filter state ---
  const rawQ = searchParams.get("q") ?? "";

  const rawStatus = searchParams.get("status");
  const status: ContactStatusFilter = Object.values(ContactStatusFilter).includes(
    rawStatus as ContactStatusFilter,
  )
    ? (rawStatus as ContactStatusFilter)
    : ContactStatusFilter.Active;

  const rawPostal = searchParams.get("postal");
  const hasPostal = rawPostal === "yes" ? true : rawPostal === "no" ? false : undefined;

  const rawEmail = searchParams.get("email");
  const hasEmail = rawEmail === "yes" ? true : rawEmail === "no" ? false : undefined;

  const rawSort = searchParams.get("sort") ?? `${ContactSortField.UpdatedAt}-${SortDirection.Desc}`;
  const [rawSortField, rawSortDir] = rawSort.split("-");
  const sort: ContactSortField = Object.values(ContactSortField).includes(
    rawSortField as ContactSortField,
  )
    ? (rawSortField as ContactSortField)
    : ContactSortField.UpdatedAt;
  const sortDir: SortDirection =
    rawSortDir === SortDirection.Asc ? SortDirection.Asc : SortDirection.Desc;

  const page = Math.max(1, parseInt(searchParams.get("page") ?? "1", 10) || 1);

  // Local state for the search input display only; flushed to URL with debounce
  const { search, setSearch } = useUrlSearchSync(searchParams, setSearchParams);

  const setParam = (key: string, value: string | null) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value !== null) next.set(key, value);
      else next.delete(key);
      next.set("page", "1");
      return next;
    });
  };

  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const [addOpen, setAddOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [addToListOpen, setAddToListOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);

  const params: ContactSearchParams = {
    q: rawQ || undefined,
    status,
    hasPostalAddress: hasPostal,
    hasEmail,
    sort,
    sortDir,
    page,
    limit: 25,
  };

  const { data, isLoading, isFetching, isPlaceholderData } = useContactsOptional(params);
  const contacts = data?.contacts ?? [];
  const total = data?.total ?? 0;

  const refresh = () => invalidate.invalidateContacts();

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === contacts.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(contacts.map((c) => c.id)));
    }
  };

  const handleBulkExportVCard = async () => {
    const toExport = selectedIds.size ? contacts.filter((c) => selectedIds.has(c.id)) : contacts;
    if (toExport.length === 0) return;
    const vcf = await contactsToVCardFileAsync(toExport);
    const blob = new Blob([vcf], { type: "text/vcard;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "contacts.vcf";
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleBulkMarkInactive = async () => {
    if (selectedIds.size === 0) return;
    await bulkUpdateMutation.mutateAsync({
      ids: [...selectedIds],
      updates: { status: "inactive" },
    });
    setSelectedIds(new Set());
    refresh();
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (
      !(await confirm(
        `Delete ${selectedIds.size} contact${selectedIds.size !== 1 ? "s" : ""}? This cannot be undone.`,
      ))
    )
      return;
    for (const id of selectedIds) {
      await deleteContactMutation.mutateAsync(id);
    }
    setSelectedIds(new Set());
    refresh();
  };

  const fetchAllContacts = async (): Promise<typeof contacts> => {
    const baseParams: ContactSearchParams = {
      q: params.q,
      status: params.status,
      hasPostalAddress: params.hasPostalAddress,
      hasEmail: params.hasEmail,
      sort: params.sort,
      sortDir: params.sortDir,
      limit: 100,
    };
    const all: typeof contacts = [];
    let p = 1;
    let hasMore = true;
    while (hasMore) {
      const result = await fetchContactsList({ ...baseParams, page: p });
      all.push(...result.contacts);
      hasMore = result.contacts.length === 100 && all.length < result.total;
      p++;
    }
    return all;
  };

  const handleExportCsv = async () => {
    const allContacts = await fetchAllContacts();
    downloadContactsCsv(allContacts);
  };

  const handleExportPdf = async () => {
    const allContacts = await fetchAllContacts();
    downloadContactsPdf(allContacts);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Contacts</h1>
          <p className="text-muted-foreground mt-1">
            Unified directory for people and organizations. Manage mailing lists for events.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline">
                Actions
                <ChevronDown className="size-4 ml-2" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setAddOpen(true)}>
                <Plus className="size-4" />
                Add Contact
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setInviteOpen(true)}>
                <UserPlus className="size-4" />
                Invite to Sign Up
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setImportOpen(true)}>
                <Upload className="size-4" />
                Import
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/admin/contacts/lists">
                  <List className="size-4" />
                  Mailing Lists
                </Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleExportCsv}>
                <Download className="size-4" />
                Export CSV
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleExportPdf}>
                <Download className="size-4" />
                Export PDF
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="sticky top-14 z-10 -mx-6 -mt-6 flex flex-col gap-4 border-b border-border/50 bg-card px-6 pt-6 pb-4 sm:flex-row sm:items-center sm:gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
              <Input
                placeholder="Search name, email, phone, city, tags, notes..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select
              value={status}
              onValueChange={(v) => setParam("status", v === ContactStatusFilter.Active ? null : v)}
            >
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ContactStatusFilter.Active}>Active</SelectItem>
                <SelectItem value={ContactStatusFilter.Deleted}>Deleted</SelectItem>
                <SelectItem value={ContactStatusFilter.All}>All</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={hasPostal === undefined ? "any" : hasPostal ? "yes" : "no"}
              onValueChange={(v) => setParam("postal", v === "any" ? null : v)}
            >
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="Address" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="any">Any address</SelectItem>
                <SelectItem value="yes">Has postal</SelectItem>
                <SelectItem value="no">No postal</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={hasEmail === undefined ? "any" : hasEmail ? "yes" : "no"}
              onValueChange={(v) => setParam("email", v === "any" ? null : v)}
            >
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="Email" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="any">Any email</SelectItem>
                <SelectItem value="yes">Has email</SelectItem>
                <SelectItem value="no">No email</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={`${sort}-${sortDir}`}
              onValueChange={(v) =>
                setParam(
                  "sort",
                  v === `${ContactSortField.UpdatedAt}-${SortDirection.Desc}` ? null : v,
                )
              }
            >
              <SelectTrigger className="w-[160px]">
                <SelectValue placeholder="Sort" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={`${ContactSortField.Name}-${SortDirection.Asc}`}>
                  Name A-Z
                </SelectItem>
                <SelectItem value={`${ContactSortField.Name}-${SortDirection.Desc}`}>
                  Name Z-A
                </SelectItem>
                <SelectItem value={`${ContactSortField.UpdatedAt}-${SortDirection.Desc}`}>
                  Last updated
                </SelectItem>
                <SelectItem value={`${ContactSortField.UpdatedAt}-${SortDirection.Asc}`}>
                  Oldest first
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {selectedIds.size > 0 && (
            <div className="mt-4 flex items-center gap-2 rounded-lg bg-muted p-3">
              <span className="text-sm font-medium">{selectedIds.size} selected</span>
              <Button variant="outline" size="sm" onClick={handleBulkExportVCard}>
                <Download className="size-4" />
                Export vCard
              </Button>
              <Button variant="outline" size="sm" onClick={() => setAddToListOpen(true)}>
                <List className="size-4" />
                Add to list
              </Button>
              <Button variant="outline" size="sm" onClick={handleBulkMarkInactive}>
                Mark inactive
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleBulkDelete}
                className="text-destructive hover:text-destructive"
              >
                <Trash2 className="size-4" />
                Delete
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setSelectedIds(new Set())}>
                Clear
              </Button>
            </div>
          )}

          {isLoading ? (
            <div className="py-12 text-center text-muted-foreground">Loading…</div>
          ) : contacts.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground">
              No contacts found. Add one or import from vCard/CSV.
            </div>
          ) : (
            <div className={`mt-4 transition-opacity ${isPlaceholderData ? "opacity-50" : ""}`}>
              <ContactDirectoryTable
                rows={contacts.map((c) => ({ contact: c }))}
                columns={["checkbox", "name", "phone", "address", "email", "tags", "status"]}
                selectable
                selectedIds={selectedIds}
                onToggleSelect={toggleSelect}
                onToggleSelectAll={toggleSelectAll}
                onRowClick={(c) =>
                  navigate(`/admin/contacts/${c.id}`, {
                    state: { from: location.pathname + location.search },
                  })
                }
              />
            </div>
          )}

          {total > 25 && (
            <div className="mt-4 flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                {isFetching && !isLoading ? (
                  <span className="italic">Loading…</span>
                ) : (
                  <>
                    Showing {(page - 1) * 25 + 1}–{Math.min(page * 25, total)} of {total}
                  </>
                )}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1 || isPlaceholderData}
                  onClick={() => {
                    const next = new URLSearchParams(searchParams);
                    next.set("page", String(page - 1));
                    setSearchParams(next);
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page * 25 >= total || isPlaceholderData}
                  onClick={() => {
                    const next = new URLSearchParams(searchParams);
                    next.set("page", String(page + 1));
                    setSearchParams(next);
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <InviteUserDialog open={inviteOpen} onOpenChange={setInviteOpen} />
      <AddContactDialog open={addOpen} onOpenChange={setAddOpen} onSuccess={refresh} />
      <ImportContactsDialog open={importOpen} onOpenChange={setImportOpen} onSuccess={refresh} />
      <AddToMailingListDialog
        open={addToListOpen}
        onOpenChange={setAddToListOpen}
        contactIds={[...selectedIds]}
        onSuccess={() => {
          setSelectedIds(new Set());
          refresh();
        }}
      />
      {confirmDialog}
    </div>
  );
}
